import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Trash2 } from "lucide-react";
import { cn } from "../../../../utils/cn";
import { Button } from "../../../../components/ui/Button";
import { Input } from "../../../../components/ui/Input";
import { Select } from "../../../../components/ui/Select";
import { formatPhone } from "../../../../layouts/helpdeskLayout/HelpdeskRail";
import {
  AUDIENCE_OPTIONS,
  BOOL_OPTION_LABELS,
  CONDITION_CHANNELS,
  CONDITION_DEFS,
  CONDITION_ORDER,
  CONDITION_TEXT_MAX,
  MAX_CONDITIONS,
  MAX_CONDITION_VALUES,
  OPERATOR_LABELS,
  defaultCondition,
  idOf,
  validateCondition,
} from "./workflowSchema";

// Condiciones de una regla ("Si se cumplen TODAS…"): una fila por condición con
// campo, operador y valor. En pantallas chicas la fila se apila (campo y
// operador arriba, valor abajo) en vez de comprimir tres controles en 360px.
//
// `conditions` lleva `_key` local por fila (mismo criterio que los pasos).

let conditionSeq = 0;
export function withConditionKey(condition) {
  conditionSeq += 1;
  return { ...condition, _key: `cond-${conditionSeq}` };
}

// Chips conmutables para los campos de lista (canal, cuenta, audiencia).
function MultiToggle({ options, value, onChange, ariaLabel, emptyText }) {
  const selected = new Set(Array.isArray(value) ? value : []);
  if (options.length === 0) return <p className="text-xs text-fg-muted">{emptyText}</p>;
  return (
    <div role="group" aria-label={ariaLabel} className="flex flex-wrap gap-1.5">
      {options.map((opt) => {
        const active = selected.has(opt.value);
        // Al tope solo se puede desmarcar: el backend rechaza más valores.
        const blocked = !active && selected.size >= MAX_CONDITION_VALUES;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            disabled={blocked}
            onClick={() => {
              const next = new Set(selected);
              if (active) next.delete(opt.value);
              else next.add(opt.value);
              onChange([...next]);
            }}
            className={cn(
              "inline-flex items-center h-11 md:h-7 px-2.5 rounded-full border text-xs font-medium max-w-full",
              "transition-colors duration-150 ease-out motion-reduce:transition-none",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
              active
                ? "border-brand-500 bg-brand-50 text-brand-700"
                : "border-border bg-bg text-fg-muted hover:bg-bg-muted hover:text-fg",
              blocked && "opacity-50 cursor-not-allowed hover:bg-bg hover:text-fg-muted",
            )}
          >
            <span className="truncate">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function ConditionValue({ condition, onValue, labels, accountOptions, accountsError, fieldId }) {
  const { t } = useTranslation();
  const def = CONDITION_DEFS[condition.field];

  switch (condition.field) {
    case "channel":
      return (
        <MultiToggle
          ariaLabel={t("Canales")}
          options={CONDITION_CHANNELS.map((c) => ({ value: c.value, label: t(c.label) }))}
          value={condition.value}
          onChange={onValue}
        />
      );
    case "account_key":
      return (
        <MultiToggle
          ariaLabel={t("Números o cuentas")}
          // Una cuenta guardada que ya no está vinculada sigue visible (y se
          // puede quitar) en vez de viajar oculta en la regla.
          options={[
            ...accountOptions,
            ...(Array.isArray(condition.value) ? condition.value : [])
              .filter((key) => !accountOptions.some((o) => o.value === key))
              .map((key) => ({ value: key, label: key })),
          ]}
          value={condition.value}
          onChange={onValue}
          emptyText={
            accountsError
              ? t("No se pudieron cargar los números vinculados.")
              : t("No hay números ni cuentas vinculadas.")
          }
        />
      );
    case "audience":
      return (
        <MultiToggle
          ariaLabel={t("Quién escribe")}
          options={AUDIENCE_OPTIONS.map((a) => ({ value: a.value, label: t(a.label) }))}
          value={condition.value}
          onChange={onValue}
        />
      );
    default:
      break;
  }

  if (def.kind === "text") {
    return (
      <Input
        id={fieldId}
        value={condition.value || ""}
        maxLength={CONDITION_TEXT_MAX}
        onChange={(e) => onValue(e.target.value)}
        placeholder={t("Ej: reembolso")}
        aria-label={t("Texto a buscar")}
      />
    );
  }

  if (def.kind === "label") {
    return (
      <Select
        id={fieldId}
        aria-label={t("Etiqueta")}
        value={(Array.isArray(condition.value) && condition.value[0]) || ""}
        onChange={(e) => onValue(e.target.value ? [e.target.value] : [])}
        placeholder={labels.length ? t("Elige una etiqueta") : t("No hay etiquetas creadas")}
        options={labels.map((l) => ({ value: idOf(l), label: l.name }))}
      />
    );
  }

  // bool: control segmentado de dos opciones.
  const names = BOOL_OPTION_LABELS[condition.field] || { true: "Sí", false: "No" };
  return (
    <div
      role="radiogroup"
      aria-label={t(def.label)}
      className="inline-flex rounded-lg border border-border bg-bg-muted/40 p-0.5"
    >
      {[true, false].map((option) => {
        const active = condition.value === option;
        return (
          <button
            key={String(option)}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onValue(option)}
            className={cn(
              "h-11 md:h-7 px-3 text-xs font-medium rounded-md transition-colors",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
              active ? "bg-bg text-fg shadow-sm" : "text-fg-muted hover:text-fg",
            )}
          >
            {t(names[String(option)])}
          </button>
        );
      })}
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array<{_key:string, field:string, operator:string, value:any}>} props.conditions
 * @param {(next:Array)=>void} props.onChange
 * @param {Array} props.labels
 * @param {Array|null} props.accounts  cuentas {key,label,phone} del alcance de la IA
 * @param {boolean} props.accountsError
 * @param {boolean} props.showErrors
 */
export default function ConditionsEditor({ conditions, onChange, labels, accounts, accountsError, showErrors }) {
  const { t } = useTranslation();
  const full = conditions.length >= MAX_CONDITIONS;

  // Cuentas únicas por la clave que arma el BACKEND (alcance de la IA:
  // evo:/wa:/page:), la misma que compara la regla; nunca se construye acá.
  const accountOptions = useMemo(() => {
    const seen = new Map();
    (accounts || []).forEach((account) => {
      const key = account?.key;
      if (!key || seen.has(key)) return;
      seen.set(key, account.label || formatPhone(account.phone) || key);
    });
    return [...seen.entries()].map(([value, label]) => ({ value, label }));
  }, [accounts]);

  const update = (index, patch) =>
    onChange(conditions.map((c, i) => (i === index ? { ...c, ...patch } : c)));

  const changeField = (index, field) =>
    onChange(
      conditions.map((c, i) => (i === index ? { ...defaultCondition(field), _key: c._key } : c)),
    );

  const add = (field) => {
    if (!field || full || !CONDITION_DEFS[field]) return;
    onChange([...conditions, withConditionKey(defaultCondition(field))]);
  };

  return (
    <div className="space-y-2">
      {conditions.length === 0 && (
        <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-xs text-fg-muted">
          {t("Sin condiciones: la regla corre en cada evento.")}
        </p>
      )}

      <ul className="space-y-2" aria-label={t("Condiciones")}>
        {conditions.map((condition, index) => {
          const def = CONDITION_DEFS[condition.field];
          const error = showErrors ? validateCondition(condition, t) : null;
          const fieldId = `rule-cond-${condition._key}`;
          return (
            <li
              key={condition._key}
              className={cn("rounded-lg border bg-bg p-3", error ? "border-status-error" : "border-border")}
            >
              <div className="flex flex-col md:flex-row md:items-start gap-2">
                <div className="flex items-center gap-2 md:w-[340px] shrink-0">
                  {index > 0 && (
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-fg-muted w-6 shrink-0">
                      {t("y")}
                    </span>
                  )}
                  <div className="flex-1 min-w-0">
                    <Select
                      aria-label={t("Campo")}
                      value={condition.field}
                      onChange={(e) => changeField(index, e.target.value)}
                      options={CONDITION_ORDER.map((field) => ({
                        value: field,
                        label: t(CONDITION_DEFS[field].label),
                      }))}
                    />
                  </div>
                  <div className="w-[130px] shrink-0">
                    {def.operators.length > 1 ? (
                      <Select
                        aria-label={t("Operador")}
                        value={condition.operator}
                        onChange={(e) => update(index, { operator: e.target.value })}
                        options={def.operators.map((op) => ({ value: op, label: t(OPERATOR_LABELS[op]) }))}
                      />
                    ) : (
                      <span className="block px-1 text-sm text-fg-muted">{t(OPERATOR_LABELS[def.operators[0]])}</span>
                    )}
                  </div>
                </div>
                <div className="flex-1 min-w-0 flex items-start gap-2">
                  <div className="flex-1 min-w-0 md:pt-0.5">
                    <ConditionValue
                      condition={condition}
                      onValue={(value) => update(index, { value })}
                      labels={labels}
                      accountOptions={accountOptions}
                      accountsError={accountsError}
                      fieldId={fieldId}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="px-2 h-11 md:h-8 text-status-error hover:bg-status-error-soft shrink-0"
                    onClick={() => onChange(conditions.filter((_, i) => i !== index))}
                    aria-label={t("Quitar condición")}
                  >
                    <Trash2 className="w-4 h-4" aria-hidden="true" />
                  </Button>
                </div>
              </div>
              {error && <p className="mt-1.5 text-xs text-status-error">{error}</p>}
            </li>
          );
        })}
      </ul>

      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-64">
          <Select
            aria-label={t("Agregar condición")}
            value=""
            disabled={full}
            onChange={(e) => add(e.target.value)}
            placeholder={full ? t("Tope de 10 condiciones") : t("+ Agregar condición…")}
            options={CONDITION_ORDER.map((field) => ({ value: field, label: t(CONDITION_DEFS[field].label) }))}
          />
        </div>
        <span className="text-xs text-fg-muted tabular-nums">
          {conditions.length} / {MAX_CONDITIONS}
        </span>
      </div>
    </div>
  );
}
